import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getMonthlyToAnnualUpgradeDetails } from '../api/checkoutApi';
import { getApiAxiosInstance } from '../utils/network';
import { submitMonthlyToAnnualUpgrade, getSubscriptionInfo } from '../actions/checkout-subscription-actions';
import { SubscriptionGateways } from '../components/Analytics/constants';

/**
 * Hook that manages monthly-to-annual Power upgrade flow.
 * Fetches upgrade details (pricing, card on file, renewal date) and handles upgrade submission.
 *
 * When isPastDue is true, also fetches subscription info (renewal date, card brand, last4)
 * from GET v1/subscriptions/list for the past-due warning message.
 *
 * Extracted from UpgradePowerMonthlyToAnnualPopup for reuse in SidebarPowerCheckout.
 */
const useMonthlyToAnnualUpgrade = ({ isPastDue = false, enabled = true } = {}) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth?.user);
    const isStripeSubscriber = user?.subscriptionGateway === SubscriptionGateways.STRIPE;
    const subscriptions = useSelector((state) => state.subscribe?.subscriptions);

    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [upgradeData, setUpgradeData] = useState(null);
    const [upgradeError, setUpgradeError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [binCountryError, setBinCountryError] = useState('');

    const fetchUpgradeData = async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const response = await getMonthlyToAnnualUpgradeDetails(getApiAxiosInstance());
            const data = response?.data;
            if (data) {
                setUpgradeData(data);
            } else {
                setFetchError('Unable to load upgrade details');
            }
        } catch (err) {
            setFetchError(err?.response?.data?.message || 'Unable to load upgrade details');
            setUpgradeData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (enabled && isStripeSubscriber) {
            fetchUpgradeData();
        } else {
            setLoading(false);
        }
    }, [enabled, isStripeSubscriber]);

    // Fetch past-due subscription details (renewal date, card brand, last4) when needed
    useEffect(() => {
        if (enabled && isPastDue && isStripeSubscriber) {
            dispatch(getSubscriptionInfo());
        }
    }, [enabled, isPastDue, isStripeSubscriber, dispatch]);

    const pastDueDetails = useMemo(() => {
        if (!isPastDue || !subscriptions?.length) {
            return null;
        }

        const pastDueSub = subscriptions.find((sub) => sub.isPastDue);
        if (!pastDueSub) {
            return null;
        }

        return {
            renewalDate: pastDueSub.renewalDate,
            cardBrand: pastDueSub.paymentSource?.type,
            last4: pastDueSub.paymentSource?.last4,
        };
    }, [isPastDue, subscriptions]);

    const handleBinCountryMismatch = (annualPlanData, errorMessage) => {
        setUpgradeData((prev) => {
            const next = { ...(prev || {}) };
            if (annualPlanData?.planId) {
                next.annualPlan = annualPlanData;
            } else if (annualPlanData?.annualPlan) {
                next.annualPlan = annualPlanData.annualPlan;
            }
            if (annualPlanData?.isTennisAustraliaMember !== undefined) {
                next.isTennisAustraliaMember = annualPlanData.isTennisAustraliaMember;
            }
            return next;
        });
        setBinCountryError(errorMessage);
        setUpgradeError('');
    };

    const performUpgrade = async (paymentMethodId = null, stripe = null) => {
        const planId = upgradeData?.annualPlan?.planId;
        if (!planId) {
            return null;
        }

        setSubmitting(true);
        setUpgradeError('');
        setBinCountryError('');

        try {
            const result = await dispatch(submitMonthlyToAnnualUpgrade({ // NOSONAR — Redux thunk returns a promise
                values: { planId, paymentMethodId },
                onBinCountryMismatch: handleBinCountryMismatch,
                stripe,
            }));

            if (result?.isSubscribed) {
                return { isSubscribed: true, ...result };
            }

            if (result?.error) {
                setUpgradeError(result.error);
                return { isSubscribed: false, error: result.error };
            }

            // BIN country mismatch — handled by callback, no error to show
            return { isSubscribed: false };
        } catch (err) {
            const responseData = err?.response?.data;
            if (responseData?.annualPlan) {
                handleBinCountryMismatch(
                    responseData.annualPlan,
                    responseData?.message || 'We have changed the billing details to match your billing country, please try again.',
                );
                return { isSubscribed: false };
            }

            const message = responseData?.message || err?.message || 'Something went wrong. Please update your card below.';
            setUpgradeError(message);
            return { isSubscribed: false, error: message };
        } finally {
            setSubmitting(false);
        }
    };

    const clearErrors = () => {
        setUpgradeError('');
        setBinCountryError('');
    };

    const refetchUpgradeData = () => {
        clearErrors();
        fetchUpgradeData();
    };

    return {
        loading,
        fetchError,
        upgradeData,
        upgradeError,
        submitting,
        binCountryError,
        pastDueDetails,
        performUpgrade,
        clearErrors,
        refetchUpgradeData,
    };
};

export default useMonthlyToAnnualUpgrade;
